import { create } from 'zustand';
import { env } from '@/env.js';

interface TenantState {
  tenantId: string | null;
  isOverride: boolean;
  setTenant: (id: string | null) => void;
}

/**
 * Runtime tenant identity. White-label build resolves the tenant from the
 * origin host via /site-config; the resolved tenantId lands here. When
 * VITE_TENANT_ID is set (dev/custom build) we run in override mode and send
 * X-Tenant-Id on every request (see lib/api/client.ts).
 */
export const useTenantStore = create<TenantState>((set) => ({
  tenantId: env.VITE_TENANT_ID ?? null,
  isOverride: Boolean(env.VITE_TENANT_ID),
  setTenant: (tenantId) => set({ tenantId }),
}));

export function isTenantOverride(): boolean {
  return Boolean(env.VITE_TENANT_ID);
}

/**
 * X-Tenant-Id / body tenantId value to send. Prefer the build-time override,
 * else the tenant resolved from /site-config. Frontend runs on a separate host
 * (Cloudflare Pages) from the API gateway, so the gateway can't map the custom
 * domain via Host — the resolved tenant must travel on every request.
 */
export function tenantHeaderValue(): string | null {
  return env.VITE_TENANT_ID ?? useTenantStore.getState().tenantId;
}

/** How long a request waits for /site-config before going out without a tenant. */
const TENANT_WAIT_MS = 5_000;

let resolving: Promise<void> | null = null;
let settle: (() => void) | null = null;

/**
 * Call once at boot, before /site-config is requested. If the tenant is not yet
 * known (no override, no cached snapshot), `tenantReady()` holds requests until
 * `settleTenantResolution()` — otherwise a first visit's tenant-scoped reads
 * (banners, popups, catalog) go out headerless and are answered for no brand.
 */
export function beginTenantResolution(): void {
  if (tenantHeaderValue() || resolving) return;
  resolving = new Promise<void>((resolve) => {
    settle = resolve;
  });
}

/** Call when /site-config has answered or failed; releases every waiting request. */
export function settleTenantResolution(): void {
  settle?.();
  resolving = null;
  settle = null;
}

/** Resolves at once unless boot is still resolving the tenant; never waits past `TENANT_WAIT_MS`. */
export function tenantReady(): Promise<void> {
  if (!resolving) return Promise.resolve();
  return Promise.race([resolving, new Promise<void>((resolve) => setTimeout(resolve, TENANT_WAIT_MS))]);
}
